import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Send, Eye, Edit } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { logger } from '@/lib/logger';

export function EmailNotificationManager() {
  const { toast } = useToast();
  const [testEmail, setTestEmail] = useState('derek@caldwellrg.com');
  const [emailType, setEmailType] = useState<string>('all');
  const [sending, setSending] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<string>('welcome');
  const [customTemplates, setCustomTemplates] = useState({
    welcome: '',
    reset: '',
    verify: '',
    invitation: '',
    'abandoned-1': '',
    'abandoned-2': '',
    'abandoned-3': ''
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomTemplates();
  }, []);

  const loadCustomTemplates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setCustomTemplates({
          welcome: (data as any).custom_welcome_email_template || '',
          reset: (data as any).custom_reset_email_template || '',
          verify: (data as any).custom_verify_email_template || '',
          invitation: (data as any).custom_invitation_email_template || '',
          'abandoned-1': (data as any).custom_abandoned_checkout_email_1_template || '',
          'abandoned-2': (data as any).custom_abandoned_checkout_email_2_template || '',
          'abandoned-3': (data as any).custom_abandoned_checkout_email_3_template || ''
        });
      }
    } catch (error) {
      logger.error('Error loading custom templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCustomTemplate = async () => {
    setSavingTemplate(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const columnMap = {
        welcome: 'custom_welcome_email_template',
        reset: 'custom_reset_email_template',
        verify: 'custom_verify_email_template',
        invitation: 'custom_invitation_email_template',
        'abandoned-1': 'custom_abandoned_checkout_email_1_template',
        'abandoned-2': 'custom_abandoned_checkout_email_2_template',
        'abandoned-3': 'custom_abandoned_checkout_email_3_template'
      };

      const column = columnMap[editingTemplate as keyof typeof columnMap];
      const value = customTemplates[editingTemplate as keyof typeof customTemplates];

      const { error } = await supabase
        .from('profiles')
        .update({ [column]: value || null } as any)
        .eq('user_id', user.id);

      if (error) {
        // If the columns don't exist yet, show a helpful message
        if (error.message.includes('does not exist')) {
          throw new Error('Database migration pending. Please run the migration first.');
        }
        throw error;
      }

      toast({
        title: "Template Saved",
        description: `Custom ${editingTemplate} template has been saved successfully`,
      });
    } catch (error: any) {
      logger.error('Error saving template:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save template",
        variant: "destructive",
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  const resetToDefault = () => {
    setCustomTemplates({
      ...customTemplates,
      [editingTemplate]: ''
    });
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast({
        title: "Email Required",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('test-email', {
        body: { emailType: emailType === 'all' ? undefined : emailType, testEmail }
      });

      if (error) throw error;

      toast({
        title: "Success!",
        description: data.message || `Test email(s) sent to ${testEmail}`,
      });
    } catch (error: any) {
      logger.error('Error sending test email:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send test email",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const getDefaultTemplate = (template: string) => {
    const NORTH_LOGO_URL = "https://zkqcqgjbsigjdwzmnwmp.supabase.co/storage/v1/object/public/logos/north-logo.png";

    const templates = {
      welcome: {
        subject: "Welcome to NORTH!",
        html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Welcome to NORTH!</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px; font-weight: 600;">Welcome to NORTH!</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Hi {{fullName}},</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Welcome to NORTH! We're excited to have you on board. Your account has been successfully created.</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">NORTH helps real estate professionals create stunning listing reports, track market insights, and manage social media campaigns—all in one powerful platform.</p>
              <div style="background-color: #f8f9fa; border-radius: 6px; padding: 24px; margin: 30px 0;">
                <p style="color: #333333; font-size: 16px; font-weight: 600; margin: 0 0 16px;">What you can do with NORTH:</p>
                <ul style="color: #555555; font-size: 15px; line-height: 28px; margin: 0; padding-left: 20px;">
                  <li>Generate AI-powered market reports and insights</li>
                  <li>Track comparable properties and market trends</li>
                  <li>Manage showings and open house events</li>
                  <li>Sync and analyze social media performance</li>
                  <li>Create branded, shareable seller reports</li>
                </ul>
              </div>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="{{loginUrl}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600;">Get Started</a>
                  </td>
                </tr>
              </table>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">If you have any questions, our support team is here to help.</p>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">© ${new Date().getFullYear()} NORTH. All rights reserved.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
      },
      reset: {
        subject: "Reset Your NORTH Password",
        html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Reset Your NORTH Password</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: 600;">Reset Your Password</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">We received a request to reset the password for your NORTH account associated with <strong>{{email}}</strong>.</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 30px;">Click the button below to reset your password. This link will expire in 1 hour.</p>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="{{resetUrl}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600;">Reset Password</a>
                  </td>
                </tr>
              </table>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 30px 0 0;">If the button doesn't work, copy and paste this link into your browser:</p>
              <p style="color: #667eea; font-size: 14px; line-height: 22px; word-break: break-all; margin: 10px 0 30px;">{{resetUrl}}</p>
              <div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 16px; margin: 30px 0;">
                <p style="color: #856404; font-size: 14px; margin: 0;">⚠️ If you didn't request a password reset, you can safely ignore this email. Your password will remain unchanged.</p>
              </div>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">© ${new Date().getFullYear()} NORTH. All rights reserved.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
      },
      verify: {
        subject: "Verify Your NORTH Email",
        html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Verify Your NORTH Email</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: 600;">Verify Your Email</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Thank you for signing up for NORTH! To complete your registration, please verify your email address: <strong>{{email}}</strong></p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 30px;">Click the button below to verify your email and activate your account:</p>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="{{confirmUrl}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600;">Verify Email Address</a>
                  </td>
                </tr>
              </table>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 30px 0 0;">If the button doesn't work, copy and paste this link into your browser:</p>
              <p style="color: #667eea; font-size: 14px; line-height: 22px; word-break: break-all; margin: 10px 0 30px;">{{confirmUrl}}</p>
              <div style="background-color: #d1ecf1; border-left: 4px solid #0c5460; padding: 16px; margin: 30px 0;">
                <p style="color: #0c5460; font-size: 14px; margin: 0;">ℹ️ This verification link will expire in 24 hours. If you didn't create an account with NORTH, you can safely ignore this email.</p>
              </div>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">© ${new Date().getFullYear()} NORTH. All rights reserved.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
      },
      invitation: {
        subject: "You're invited to join a team on NORTH",
        html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>You're Invited to Join a Team on NORTH!</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px; font-weight: 600;">You're Invited!</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Hi {{inviteeName}},</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;"><strong>{{inviterName}}</strong> has invited you to join their team on NORTH!</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">NORTH helps real estate professionals create stunning listing reports, track market insights, and manage social media campaigns—all in one powerful platform.</p>
              <div style="background-color: #f8f9fa; border-radius: 6px; padding: 24px; margin: 30px 0;">
                <p style="color: #333333; font-size: 16px; font-weight: 600; margin: 0 0 16px;">What you can do with NORTH:</p>
                <ul style="color: #555555; font-size: 15px; line-height: 28px; margin: 0; padding-left: 20px;">
                  <li>Generate AI-powered market reports and insights</li>
                  <li>Track comparable properties and market trends</li>
                  <li>Manage showings and open house events</li>
                  <li>Sync and analyze social media performance</li>
                  <li>Create branded, shareable seller reports</li>
                </ul>
              </div>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="{{invitationUrl}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600;">Accept Invitation & Create Account</a>
                  </td>
                </tr>
              </table>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 30px 0 0;">If the button doesn't work, copy and paste this link into your browser:</p>
              <p style="color: #667eea; font-size: 14px; line-height: 22px; word-break: break-all; margin: 10px 0 30px;">{{invitationUrl}}</p>
              <div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 16px; margin: 30px 0;">
                <p style="color: #856404; font-size: 14px; margin: 0;">⏰ This invitation will expire in 7 days. If you didn't expect this invitation, you can safely ignore this email.</p>
              </div>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">© ${new Date().getFullYear()} NORTH. All rights reserved.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
      },
      'abandoned-1': {
        subject: 'Complete Your NORTH Setup',
        html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Complete Your NORTH Setup</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px; font-weight: 600;">Let's Get You Started!</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Hi {{fullName}},</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">We noticed you started creating your NORTH account, but didn't finish selecting your plan. We'd love to help you get up and running!</p>
              <div style="background-color: #f8f9fa; border-radius: 6px; padding: 24px; margin: 30px 0;">
                <p style="color: #333333; font-size: 16px; font-weight: 600; margin: 0 0 16px;">With NORTH, you can:</p>
                <ul style="color: #555555; font-size: 15px; line-height: 28px; margin: 0; padding-left: 20px;">
                  <li>Create professional market reports in minutes</li>
                  <li>Track comparable properties automatically</li>
                  <li>Generate AI-powered insights for your listings</li>
                  <li>Sync and analyze social media performance</li>
                  <li>Impress clients with branded, shareable reports</li>
                </ul>
              </div>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="{{selectPlanUrl}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600;">Select Your Plan</a>
                  </td>
                </tr>
              </table>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Have questions? Just reply to this email and we'll help you get started.</p>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">
                <a href="{{unsubscribeUrl}}" style="color: #999999; text-decoration: underline;">Unsubscribe</a> | © ${new Date().getFullYear()} NORTH. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
      },
      'abandoned-2': {
        subject: 'Join Hundreds of Real Estate Professionals on NORTH',
        html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Join Hundreds of Real Estate Professionals on NORTH</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px; font-weight: 600;">You're Missing Out!</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Hi {{fullName}},</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Hundreds of real estate professionals are already using NORTH to create stunning reports, track market trends, and grow their business.</p>
              <div style="background-color: #e8f5e9; border-left: 4px solid #4caf50; padding: 20px; margin: 30px 0;">
                <p style="color: #2e7d32; font-size: 15px; line-height: 24px; margin: 0 0 12px;"><strong>Real agents are seeing results:</strong></p>
                <p style="color: #2e7d32; font-size: 15px; line-height: 24px; margin: 0;">"NORTH helped me close 3 more listings this quarter by impressing sellers with professional market reports."</p>
              </div>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Don't let your competition get ahead. Complete your setup and start creating reports that win listings today.</p>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="{{selectPlanUrl}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600;">Complete Your Setup Now</a>
                  </td>
                </tr>
              </table>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Questions? Reply to this email - we're here to help!</p>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">
                <a href="{{unsubscribeUrl}}" style="color: #999999; text-decoration: underline;">Unsubscribe</a> | © ${new Date().getFullYear()} NORTH. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
      },
      'abandoned-3': {
        subject: 'Last Chance: Complete Your NORTH Setup',
        html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Last Chance: Complete Your NORTH Setup</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px; font-weight: 600;">This Is Your Last Reminder</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Hi {{fullName}},</p>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">We've sent you a few reminders, and this will be our last one. We don't want you to miss out on what NORTH can do for your business.</p>
              <div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 20px; margin: 30px 0;">
                <p style="color: #856404; font-size: 15px; line-height: 24px; margin: 0 0 12px;"><strong>What you're missing:</strong></p>
                <ul style="color: #856404; font-size: 15px; line-height: 28px; margin: 0; padding-left: 20px;">
                  <li>AI-powered market insights that win listings</li>
                  <li>Professional reports your clients will love</li>
                  <li>Time saved on research and report creation</li>
                  <li>A competitive edge in your market</li>
                </ul>
              </div>
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 30px;">Starting at just $67/month, NORTH pays for itself with just one additional listing. That's less than the cost of one open house sign.</p>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="{{selectPlanUrl}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 6px; font-size: 16px; font-weight: 600;">Choose Your Plan Now</a>
                  </td>
                </tr>
              </table>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 30px 0 0;">If you've changed your mind, no hard feelings! Just let us know if there's anything we can do to help.</p>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 20px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">
                <a href="{{unsubscribeUrl}}" style="color: #999999; text-decoration: underline;">Unsubscribe</a> | © ${new Date().getFullYear()} NORTH. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
      },
    };

    const templateData = templates[template as keyof typeof templates];
    if (!templateData) {
      // Return a default template if not found
      return {
        subject: 'Email Template',
        html: '<p>Template not found</p>'
      };
    }
    return templateData;
  };

  const getCurrentTemplate = () => {
    const customTemplate = customTemplates[editingTemplate as keyof typeof customTemplates];
    if (customTemplate) {
      return customTemplate;
    }
    return getDefaultTemplate(editingTemplate).html;
  };

  const getPreviewHtml = () => {
    const template = getCurrentTemplate();
    // Replace variables with sample data for preview
    return template
      .replace(/{{fullName}}/g, 'John Doe')
      .replace(/{{email}}/g, 'john.doe@example.com')
      .replace(/{{loginUrl}}/g, '#')
      .replace(/{{resetUrl}}/g, '#')
      .replace(/{{confirmUrl}}/g, '#')
      .replace(/{{inviterName}}/g, 'Jane Smith')
      .replace(/{{inviteeName}}/g, 'John Doe')
      .replace(/{{invitationUrl}}/g, '#')
      .replace(/{{selectPlanUrl}}/g, 'https://northreports.com/select-plan')
      .replace(/{{unsubscribeUrl}}/g, '#');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="test" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="test">
            <Send className="mr-2 h-4 w-4" />
            Test Emails
          </TabsTrigger>
          <TabsTrigger value="templates">
            <Edit className="mr-2 h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="test" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Send Test Emails
              </CardTitle>
              <CardDescription>
                Test the email delivery system by sending sample emails to any address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test-email">Test Email Address</Label>
                <Input
                  id="test-email"
                  type="email"
                  placeholder="derek@caldwellrg.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-type">Email Type</Label>
                <Select value={emailType} onValueChange={setEmailType}>
                  <SelectTrigger id="email-type">
                    <SelectValue placeholder="Select email type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Templates (7 emails)</SelectItem>
                    <SelectItem value="welcome">Welcome Email</SelectItem>
                    <SelectItem value="reset">Password Reset</SelectItem>
                    <SelectItem value="verify">Email Verification</SelectItem>
                    <SelectItem value="invitation">Team Invitation</SelectItem>
                    <SelectItem value="abandoned-1">Abandoned Checkout - Email 1 (Day 1)</SelectItem>
                    <SelectItem value="abandoned-2">Abandoned Checkout - Email 2 (Day 3)</SelectItem>
                    <SelectItem value="abandoned-3">Abandoned Checkout - Email 3 (Day 7)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleSendTestEmail} 
                disabled={sending}
                className="w-full"
              >
                {sending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Test Email
                  </>
                )}
              </Button>

              <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                <p className="font-medium mb-2">Email Configuration:</p>
                <ul className="space-y-1">
                  <li>• Domain: northreports.com</li>
                  <li>• From: info@northreports.com</li>
                  <li>• Provider: Resend</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>
                Customize email templates with HTML. Editing is optional - leave empty to use beautifully designed defaults.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertDescription>
                  <strong>Available Variables:</strong><br />
                  • Welcome: <code className="text-xs bg-muted px-1 rounded">{'{{fullName}}'}</code>, <code className="text-xs bg-muted px-1 rounded">{'{{loginUrl}}'}</code><br />
                  • Reset: <code className="text-xs bg-muted px-1 rounded">{'{{resetUrl}}'}</code>, <code className="text-xs bg-muted px-1 rounded">{'{{email}}'}</code><br />
                  • Verify: <code className="text-xs bg-muted px-1 rounded">{'{{confirmUrl}}'}</code>, <code className="text-xs bg-muted px-1 rounded">{'{{email}}'}</code><br />
                  • Invitation: <code className="text-xs bg-muted px-1 rounded">{'{{invitationUrl}}'}</code>, <code className="text-xs bg-muted px-1 rounded">{'{{inviterName}}'}</code>, <code className="text-xs bg-muted px-1 rounded">{'{{inviteeName}}'}</code><br />
                  • Abandoned Checkout: <code className="text-xs bg-muted px-1 rounded">{'{{fullName}}'}</code>, <code className="text-xs bg-muted px-1 rounded">{'{{selectPlanUrl}}'}</code>, <code className="text-xs bg-muted px-1 rounded">{'{{unsubscribeUrl}}'}</code>
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="template-select">Select Template</Label>
                <Select value={editingTemplate} onValueChange={setEditingTemplate}>
                  <SelectTrigger id="template-select">
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="welcome">Welcome Email</SelectItem>
                    <SelectItem value="reset">Password Reset</SelectItem>
                    <SelectItem value="verify">Email Verification</SelectItem>
                    <SelectItem value="invitation">Team Invitation</SelectItem>
                    <SelectItem value="abandoned-1">Abandoned Checkout - Email 1 (Day 1)</SelectItem>
                    <SelectItem value="abandoned-2">Abandoned Checkout - Email 2 (Day 3)</SelectItem>
                    <SelectItem value="abandoned-3">Abandoned Checkout - Email 3 (Day 7)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="template-content">HTML Template</Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={resetToDefault}
                      disabled={!customTemplates[editingTemplate as keyof typeof customTemplates]}
                    >
                      Reset to Default
                    </Button>
                  </div>
                  <Textarea
                    id="template-content"
                    value={getCurrentTemplate()}
                    onChange={(e) => setCustomTemplates({
                      ...customTemplates,
                      [editingTemplate]: e.target.value
                    })}
                    placeholder="Enter custom HTML template or leave empty to use default..."
                    rows={20}
                    className="font-mono text-xs"
                  />
                  <Button 
                    onClick={saveCustomTemplate} 
                    disabled={savingTemplate}
                    className="w-full"
                  >
                    {savingTemplate ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Template'
                    )}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Email Preview</Label>
                  <div className="rounded-lg border bg-muted/30 overflow-hidden">
                    <div className="bg-background border-b px-4 py-2">
                      <p className="text-sm font-medium">Subject: {getDefaultTemplate(editingTemplate).subject}</p>
                    </div>
                    <div className="overflow-auto max-h-[500px]">
                      <iframe
                        srcDoc={getPreviewHtml()}
                        className="w-full min-h-[500px] border-0"
                        title="Email Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                <p className="font-medium mb-2">Tips:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Use standard HTML for email-safe templates</li>
                  <li>• Variables will be replaced with actual data when emails are sent</li>
                  <li>• Leave empty or click "Reset to Default" to use the beautiful default NORTH template</li>
                  <li>• Test your changes using the Test Emails tab</li>
                  <li>• Changes are saved to your profile and applied to all future emails</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
